<template>
  <div class="doughnut-reports">
    <div class="doughnut-reports__wrapper">
      <Doughnut
        id="doughnut-reports-chart-id"
        :options="chartOptions"
        :data="extractedData"
      />
    </div>
    <div class="doughnut-reports__legend">
      <div
        v-for="(item, index) in chartData"
        class="doughnut-reports__legend-item"
        @click="redirectToSingleReport(item.thread_id)"
        :key="item.title"
      >
        <span
          class="doughnut-reports__legend-item__color"
          :style="{ backgroundColor: colors[index] }"
        />
        <span class="doughnut-reports__legend-item__title">{{ item.title }}</span>
      </div>
    </div>
    <div class="doughnut-reports__no-data" v-if="!isLoading && chartData?.length === 0">
      <p>
        No top deals found for the selected filters.
      </p>

      <div>
        Enhance your results by changing filters!
      </div>
    </div>
  </div>
</template>

<script>
import { Doughnut } from 'vue-chartjs'
import { Chart as ChartJS,  Tooltip, Legend, ArcElement } from 'chart.js'

ChartJS.register(Tooltip, Legend, ArcElement)

export default {
  name: 'BarChart',
  components: { Doughnut },
  props: {
    chartData: {
      type: Array,
      required: true,
    },
    valueString: {
      type: String,
      default: 'value',
    },
    titleString: {
      type: String,
      default: 'title',
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      chartOptions: {
        responsive: true,
        borderWidth: 0,
        width: '80%',
        cutout: '58%',
        onClick: this.handleChartClick,
        // parsing: {
        //   key: this.valueString, // here we are telling chart.js that we want to use some string as the key for the chart data
        // },
        plugins: {
          legend: {
            display: false,
          },
          tooltip: {
            // Disable the on-canvas tooltip
            enabled: false,

            // creating custom tooltip
            external: (context) => {
              // Tooltip Element
              let tooltipEl = document.getElementById('doughnut-reports-tooltip');

              // Create element on first render
              if (!tooltipEl) {
                tooltipEl = document.createElement('div');
                tooltipEl.id = 'doughnut-reports-tooltip';
                tooltipEl.className = 'doughnut-reports__tooltip';
                document.body.appendChild(tooltipEl);
              }

              // Hide if no tooltip
              const tooltipModel = context.tooltip;
              if (tooltipModel.opacity === 0 || this.chartData?.length === 0) {
                tooltipEl.style.opacity = 0;
                tooltipEl.style.display = 'none';
                return;
              }

              // Set caret Position
              tooltipEl.classList.remove('above', 'below', 'no-transform');
              if (tooltipModel.yAlign) {
                tooltipEl.classList.add(tooltipModel.yAlign);
              } else {
                tooltipEl.classList.add('no-transform');
              }

              function getBody(bodyItem) {
                return bodyItem.lines;
              }

              // Set Text
              if (tooltipModel.body) {
                const titleLines = tooltipModel.title || [];
                const bodyLines = tooltipModel.body.map(getBody);

                let innerHtml = '<div class="doughnut-reports__tooltip-header">';

                titleLines.forEach(function(title) {
                  if (title.length > 60) {
                    innerHtml += '<p>' + title.substring(0,60) + '...</p>';
                  } else {
                    innerHtml += '<p>' + title + '</p>';
                  }
                });
                innerHtml += '</div><div class="doughnut-reports__tooltip-body">';

                bodyLines.forEach(function(body) {
                  // const label = tooltipModel.dataPoints[i].dataset.labels;
                  const item = `
                    <div class="doughnut-reports__tooltip-body-item">
                        <span class="doughnut-reports__tooltip-body-label">
                            CTR
                        </span>
                        <span class="doughnut-reports__tooltip-body-value">
                            ${body}%
                        </span>
                    </div>
                  `;
                  innerHtml += item;
                });
                innerHtml += '</div>';// closing tooltip body (chart-reports__tooltip-body)

                tooltipEl.innerHTML = innerHtml
              }

              const position = context.chart.canvas.getBoundingClientRect();

              // Display, position, and set styles for font
              tooltipEl.style.left = position.left + window.pageXOffset + tooltipModel.caretX + 'px';
              tooltipEl.style.top = position.top + window.pageYOffset + tooltipModel.caretY + 'px';
              tooltipEl.style.transform = 'translate(-100%, 0)';
              tooltipEl.style.opacity = 1;
              tooltipEl.style.display = 'block';
            }
          },
        }
      },
      colors: ['#0051A8', '#0072EB', '#61ADFF']
    }
  },
  beforeUnmount() {
    document.getElementById('doughnut-reports-tooltip').style.display = 'none';
  },
  computed: {
    dataInPercentage() {
      const total = this.chartData.reduce((acc, item) => acc + item[this.valueString], 0);
      return this.chartData.map((item) => Math.round((item[this.valueString] / total) * 100));
    },
    extractedData() {
      if (this.chartData?.length && !this.isLoading) {
        return {
          labels: this.chartData.map((item) => item[this.titleString]),
          datasets: [
            {
              backgroundColor: this.colors,
              data: this.dataInPercentage
            }
          ]
        }
      } else {
        return {
          labels: ['No data'],
          datasets: [
            {
              backgroundColor: ['#F5F5F5'],
              data: [1]
            }
          ]
        }
      }
    },
  },
  methods: {
    handleChartClick(event, chartElements, chart) {
      if (chartElements?.length > 0) {
        // Clicked on a chart element
        const clickedElement = chartElements[0];
        const datasetIndex = clickedElement.datasetIndex;
        const index = clickedElement.index;

        const data = chart.data.datasets[datasetIndex].data[index];

        this.redirectToSingleReport(data.thread_id);
      }
    },
    redirectToSingleReport(id) {
      this.$router.push({ name: 'reports-details', params: { id } });
    },
  },
}
</script>

<style lang="scss">
.doughnut-reports {
  display: flex;
  flex-direction: column;
}

.doughnut-reports__wrapper {
  display: flex;
  margin: 0 auto;
  justify-content: center;
  width: 90%;
}

.doughnut-reports__legend {
  display: flex;
  flex-direction: column;
  gap: 14px;
  margin-top: 24px;
}

.doughnut-reports__legend-item {
  align-items: center;
  cursor: pointer;
  display: flex;
}

.doughnut-reports__legend-item__color {
  border-radius: 50%;
  margin-right: 8px;
  min-height: 9px;
  min-width: 9px;
}

.doughnut-reports__legend-item__title {
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 1.5;

  overflow: hidden;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2; /* Number of lines to show */
}

.doughnut-reports__no-data {
  >p {
    font-size: 18px;
    font-weight: 700;
    text-align: center;
  }

  >div {
    font-size: 14px;
    font-weight: 400;
    text-align: center;
  }
}

.doughnut-reports__tooltip {
  opacity: 1;
  position: absolute;
  background-color: rgb(var(--v-theme-surface));
  color: rgb(var(--v-theme-on-surface));
  border-radius: 8px;
  box-shadow: 0 4px 20px 0 rgba(var(--v-theme-on-surface), 0.25);
  padding: 16px;
  pointer-events: none;
  max-width: 180px;
  width: 100%;
}

.doughnut-reports__tooltip-header p {
  font-size: 12px;
  font-style: normal;
  font-weight: 700;
  line-height: normal;
}

.doughnut-reports__tooltip-body {
  display: flex;
  flex-direction: column;
}

.doughnut-reports__tooltip-body-item {
  display: flex;
  margin-top: 12px;
  gap: 12px;
}

.doughnut-reports__tooltip-body-label {
  font-size: 10px;
  font-style: normal;
  font-weight: 400;
  line-height: 1.5;
}

.doughnut-reports__tooltip-body-value {
  font-size: 10px;
  font-style: normal;
  font-weight: 700;
  line-height: 1.5;
}
</style>
